/**
 * "Enrich my prompt": sends the brief already on screen to the model and offers back an
 * art-directed version for review.
 *
 * Deliberately lighter than its sibling `CampaignContextPanel`. That one owns a whole second
 * input — pasted report copy that exists nowhere else — so it earns a panel that expands. This
 * one owns no input at all: it is an *action on the brief already there*. So it is a button
 * next to that brief, and `prompt` arrives as a read-only prop rather than being copied into
 * local state; copying it would create a second version of the same text that could fall out
 * of sync with the textarea the user is still typing in.
 *
 * **No guardrail check on the input here**, unlike the campaign panel's note on pasted copy:
 * the input *is* the page's brief, which already has a gate rendered directly beneath it, and
 * a second warning on the identical string would be noise. The output is checked, inside
 * `PromptReviewCard`, because enrichment can introduce trigger words the rough text never had.
 */

import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { useState } from "react";

import { useCraftPrompt } from "@/api/queries";
import type { GuardrailConfig } from "@/guardrail/rules";

import { assistFailureMessage } from "./assistFailure";
import { PromptReviewCard } from "./PromptReviewCard";

/**
 * The only failure copy specific to this panel. Everything else — 401, 403, network, 5xx —
 * comes from the transport's own message, which is what stops this panel restating the
 * allowlist wording `messages.ts` already owns.
 */
const ENRICH_FAILURE_MESSAGE = "Couldn't enrich that brief just now. Please try again.";

export interface PromptCraftPanelProps {
  /** The current brief, owned by the page. Read-only input — it is what gets enriched. */
  prompt: string;
  /** The page overwrites its brief with the approved text; its own gate re-evaluates it. */
  onPromptApproved: (prompt: string) => void;
  /** True while a generation is running — the panel must not compete with it. */
  disabled?: boolean;
  config: GuardrailConfig | undefined;
}

export function PromptCraftPanel({
  prompt,
  onPromptApproved,
  disabled = false,
  config,
}: PromptCraftPanelProps) {
  /** `null` = no draft in play. `""` while the request is in flight. */
  const [draft, setDraft] = useState<string | null>(null);
  const craft = useCraftPrompt();

  const canSubmit = !craft.isPending && !disabled && prompt.trim().length > 0;

  function handleEnrich() {
    // Show the review card immediately, in its loading state, rather than leaving the user
    // looking at an unchanged form while the model works.
    setDraft("");
    craft.mutate(prompt, {
      onSuccess: (data) => setDraft(data.prompt),
      onError: () => setDraft(null),
    });
  }

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={craft.isPending ? undefined : <AutoAwesomeOutlined />}
          loading={craft.isPending}
          disabled={!canSubmit}
          onClick={handleEnrich}
        >
          Enrich my prompt
        </Button>
        <Typography variant="caption" color="text.secondary">
          {prompt.trim().length === 0
            ? "Write a rough brief above and we'll art-direct it for you."
            : "We'll add lighting, composition and mood, you approve it before it's used."}
        </Typography>
      </Stack>

      {craft.isError ? (
        <Alert severity="error" role="alert">
          {assistFailureMessage(craft.error, ENRICH_FAILURE_MESSAGE)}
        </Alert>
      ) : null}

      {draft !== null ? (
        <PromptReviewCard
          label="Enriched prompt"
          draft={draft}
          onDraftChange={setDraft}
          loading={craft.isPending}
          config={config}
          onApprove={() => {
            onPromptApproved(draft.trim());
            // The draft has served its purpose; the page owns the text now.
            setDraft(null);
          }}
          // Discard leaves the page's brief untouched — the user keeps the rough text they
          // wrote. That asymmetry with approve is the point of the review stage: the cost of
          // not liking an enrichment must be zero.
          onDiscard={() => setDraft(null)}
        />
      ) : null}
    </Stack>
  );
}
